/**
 * Venture — FX layer.
 * Purely additive interactive systems: scroll progress bar, magnetic buttons,
 * cursor spotlight, count-up numbers, marquee, draggable compare slider.
 * Mirrors the rAF-throttled / matchMedia patterns already used in theme.js.
 */
(function () {
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(pointer: fine)').matches;

  /* --- Scroll progress bar --- */
  (function scrollProgress() {
    var bar = document.createElement('div');
    bar.className = 'scroll-progress';
    bar.innerHTML = '<div class="scroll-progress__bar"></div>';
    document.body.appendChild(bar);
    var fill = bar.querySelector('.scroll-progress__bar');
    var raf = null;
    function update() {
      raf = null;
      var doc = document.documentElement;
      var scrollTop = doc.scrollTop || document.body.scrollTop;
      var height = doc.scrollHeight - doc.clientHeight;
      var pct = height > 0 ? (scrollTop / height) * 100 : 0;
      fill.style.width = pct + '%';
    }
    update();
    window.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(update);
    }, { passive: true });
    window.addEventListener('resize', update);
  })();

  /* --- Magnetic buttons --- */
  if (!reduced && finePointer) {
    document.querySelectorAll('[data-magnetic]').forEach(function (el) {
      var strength = 0.35;
      el.addEventListener('mousemove', function (e) {
        var rect = el.getBoundingClientRect();
        var relX = e.clientX - rect.left - rect.width / 2;
        var relY = e.clientY - rect.top - rect.height / 2;
        el.style.transform = 'translate(' + (relX * strength).toFixed(1) + 'px,' + (relY * strength).toFixed(1) + 'px)';
      });
      el.addEventListener('mouseleave', function () {
        el.style.transform = '';
      });
    });
  }

  /* --- Cursor spotlight --- */
  if (finePointer) {
    document.querySelectorAll('[data-spotlight]').forEach(function (el) {
      var raf = null;
      el.addEventListener('mousemove', function (e) {
        if (raf) return;
        raf = requestAnimationFrame(function () {
          var rect = el.getBoundingClientRect();
          el.style.setProperty('--spot-x', (e.clientX - rect.left) + 'px');
          el.style.setProperty('--spot-y', (e.clientY - rect.top) + 'px');
          raf = null;
        });
      });
      el.addEventListener('mouseenter', function () { el.classList.add('is-spotlit'); });
      el.addEventListener('mouseleave', function () { el.classList.remove('is-spotlit'); });
    });
  }

  /* --- Count-up numbers --- */
  (function countUp() {
    var els = document.querySelectorAll('[data-count-to]');
    if (!els.length) return;

    function animate(el) {
      var target = parseFloat(el.getAttribute('data-count-to'));
      if (isNaN(target)) return;
      var suffix = el.getAttribute('data-count-suffix') || '';
      var prefix = el.getAttribute('data-count-prefix') || '';
      var decimals = parseInt(el.getAttribute('data-count-decimals'), 10) || 0;
      var duration = parseInt(el.getAttribute('data-count-duration'), 10) || 1400;

      if (reduced) {
        el.textContent = prefix + target.toFixed(decimals) + suffix;
        return;
      }

      var start = null;
      function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
      function step(ts) {
        if (!start) start = ts;
        var progress = Math.min((ts - start) / duration, 1);
        var eased = easeOutCubic(progress);
        var value = target * eased;
        el.textContent = prefix + value.toFixed(decimals) + suffix;
        if (progress < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }

    if (!('IntersectionObserver' in window)) {
      els.forEach(animate);
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          animate(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });
    els.forEach(function (el) { observer.observe(el); });
  })();

  /* --- Marquee --- */
  document.querySelectorAll('[data-marquee]').forEach(function (marquee) {
    var track = marquee.querySelector('[data-marquee-track]');
    if (!track) return;
    // Duplicate track content once for a seamless loop.
    var clone = track.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    track.parentNode.appendChild(clone);
    clone.classList.add('marquee__track');
    if (track.classList.contains('marquee__track--reverse')) clone.classList.add('marquee__track--reverse');
    // The scroll-reveal system (theme.js) already ran and only observes the
    // original nodes — strip its classes from the clone so duplicated cards
    // don't get stuck at opacity:0 waiting for an observer that never fires.
    clone.querySelectorAll('.reveal').forEach(function (el) {
      el.classList.remove('reveal', 'is-visible', 'reveal-left', 'reveal-right');
      el.style.transitionDelay = '';
    });
    clone.classList.remove('reveal', 'is-visible');

    var pause = function () { marquee.classList.add('is-paused'); };
    var resume = function () { marquee.classList.remove('is-paused'); };
    marquee.addEventListener('mouseenter', pause);
    marquee.addEventListener('mouseleave', resume);
    marquee.addEventListener('touchstart', pause, { passive: true });
    marquee.addEventListener('touchend', resume, { passive: true });
  });

  /* --- Draggable compare slider --- */
  document.querySelectorAll('[data-compare-slider]').forEach(function (slider) {
    var handle = slider.querySelector('[data-compare-handle]');
    var pos = 50;

    function setPos(value) {
      pos = Math.min(100, Math.max(0, value));
      slider.style.setProperty('--compare-pos', pos + '%');
      if (handle) handle.setAttribute('aria-valuenow', Math.round(pos));
    }
    setPos(50);

    function posFromClientX(clientX) {
      var rect = slider.getBoundingClientRect();
      return ((clientX - rect.left) / rect.width) * 100;
    }

    var dragging = false;
    function onMove(clientX) {
      if (!dragging) return;
      setPos(posFromClientX(clientX));
    }

    slider.addEventListener('mousedown', function (e) {
      dragging = true;
      setPos(posFromClientX(e.clientX));
    });
    window.addEventListener('mousemove', function (e) { onMove(e.clientX); });
    window.addEventListener('mouseup', function () { dragging = false; });

    slider.addEventListener('touchstart', function (e) {
      dragging = true;
      onMove(e.touches[0].clientX);
    }, { passive: true });
    slider.addEventListener('touchmove', function (e) {
      onMove(e.touches[0].clientX);
    }, { passive: true });
    slider.addEventListener('touchend', function () { dragging = false; });

    if (handle) {
      handle.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { setPos(pos - 5); e.preventDefault(); }
        if (e.key === 'ArrowRight') { setPos(pos + 5); e.preventDefault(); }
        if (e.key === 'Home') { setPos(0); e.preventDefault(); }
        if (e.key === 'End') { setPos(100); e.preventDefault(); }
      });
    }
  });

  /* --- Extend the existing tilt+gloss pattern to bento cards --- */
  function enableTilt(el) {
    if (!el) return;
    var raf = null;
    el.addEventListener('mousemove', function (e) {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        var rect = el.getBoundingClientRect();
        var relX = (e.clientX - rect.left) / rect.width - 0.5;
        var relY = (e.clientY - rect.top) / rect.height - 0.5;
        el.style.setProperty('--tilt-y', (relX * 5).toFixed(2) + 'deg');
        el.style.setProperty('--tilt-x', (relY * -5).toFixed(2) + 'deg');
        el.style.setProperty('--gloss-x', (50 + relX * 60).toFixed(1) + '%');
        el.style.setProperty('--gloss-y', (50 + relY * 60).toFixed(1) + '%');
        raf = null;
      });
    });
    el.addEventListener('mouseleave', function () {
      ['--tilt-x', '--tilt-y', '--gloss-x', '--gloss-y'].forEach(function (p) { el.style.removeProperty(p); });
    });
  }
  if (!reduced && finePointer) {
    document.querySelectorAll('.bento-card, .pillar, .tilt-card').forEach(enableTilt);
  }

  /* --- Exit-intent cart offer ---
     Two triggers, two different repeat rules:
     - Leaving the whole page (cursor darts toward the browser chrome): fires
       once per session, desktop pointer only — repeating this one would be
       annoying since it can misfire on ordinary mouse movement.
     - Closing the cart drawer without checking out (cart.js calls
       show(true)): fires EVERY time, every open/close cycle, on any device —
       each time is a deliberate "I decided not to buy yet" signal worth
       reminding them about.
     Re-queries the modal by id on every call instead of caching it, because
     cart.js replaces #ExitIntentModal's node wholesale on every cart change
     (see its refreshDrawerMarkup). */
  var exitIntentShown = function () { return sessionStorage.getItem('ventureExitIntentShown') === '1'; };

  var showExitIntent = function (force) {
    if (!force && exitIntentShown()) return;
    if (document.body.classList.contains('venture-lock-scroll')) return;
    var modal = document.getElementById('ExitIntentModal');
    if (!modal) return;
    var cartCount = document.getElementById('CartCount');
    var count = cartCount ? parseInt(cartCount.textContent, 10) || 0 : 0;
    if (count === 0) return;
    modal.hidden = false;
    document.body.classList.add('venture-lock-scroll');
    sessionStorage.setItem('ventureExitIntentShown', '1');
    document.removeEventListener('mouseout', onMouseOut);
  };

  var hideExitIntent = function () {
    var modal = document.getElementById('ExitIntentModal');
    if (!modal) return;
    modal.hidden = true;
    document.body.classList.remove('venture-lock-scroll');
  };

  var onMouseOut = function (e) {
    if (!e.relatedTarget && e.clientY <= 0) showExitIntent();
  };

  // Desktop trick: cursor darting toward the browser chrome to leave the page.
  if (finePointer) {
    document.addEventListener('mouseout', onMouseOut);
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-exit-intent-close]')) hideExitIntent();
  });
  document.addEventListener('keydown', function (e) {
    var modal = document.getElementById('ExitIntentModal');
    if (e.key === 'Escape' && modal && !modal.hidden) hideExitIntent();
  });

  // Exposed so cart.js can also trigger this when the cart drawer is closed
  // without checking out — a second, device-agnostic abandonment signal that
  // (unlike the page-leave trick above) is allowed to repeat every time.
  window.VentureExitIntent = { show: function () { showExitIntent(true); }, hide: hideExitIntent };

  /* --- "Offer ends today" countdown ---
     Resets to midnight local time on every visit. Global (not product-page
     -only) so it can run on both the hero and the product page; scoped per
     instance in case more than one ever appears on the same page. */
  document.querySelectorAll('[data-countdown-eod]').forEach(function (wrap) {
    var clock = wrap.querySelector('[data-countdown-clock]');
    if (!clock) return;

    function pad(n) { return String(n).padStart(2, '0'); }

    function tick() {
      var now = new Date();
      var midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
      var diff = Math.max(0, midnight - now);
      var h = Math.floor(diff / 3600000);
      var m = Math.floor((diff % 3600000) / 60000);
      var s = Math.floor((diff % 60000) / 1000);
      clock.textContent = pad(h) + ':' + pad(m) + ':' + pad(s);
      if (diff <= 0) { clearInterval(timer); }
    }
    tick();
    var timer = setInterval(tick, 1000);
  });

  /* --- Low-stock indicator ---
     Random unit count within a merchant-configured range, generated once per
     product per visitor and persisted in localStorage so it doesn't change
     on reload (or look suspicious by re-rolling every visit). */
  (function stockIndicator() {
    var el = document.getElementById('ProductStock');
    if (!el) return;
    var textEl = el.querySelector('[data-stock-text]');
    if (!textEl) return;
    var handle = el.getAttribute('data-product-handle') || 'default';
    var min = parseInt(el.getAttribute('data-stock-min'), 10) || 5;
    var max = parseInt(el.getAttribute('data-stock-max'), 10) || 15;
    var key = 'ventureStock:' + handle;
    var value;
    try {
      value = parseInt(localStorage.getItem(key), 10);
      if (!value || isNaN(value)) {
        value = Math.floor(Math.random() * (max - min + 1)) + min;
        localStorage.setItem(key, value);
      }
    } catch (e) {
      value = Math.floor(Math.random() * (max - min + 1)) + min;
    }
    textEl.textContent = 'Only ' + value + ' left in stock at this price';
    el.hidden = false;
  })();
})();
